import { Box, Button, Checkbox, Dialog, IconButton, MenuItem, Select, Typography, Stack, TextField, InputAdornment, Tooltip, Fab, DialogContent, Divider } from "@mui/material"
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useCallback, useEffect, useState } from "react";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import { showSnackbar } from "../../../redux/actions";
import axios from "axios";
import { urlAPI } from "../../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import SubcontractData from "./SubcontractData";
import AddUpdateSubcontract from "../AddUpdate/AddUpdateSubcontract";
import { duplicateSubcontract } from "../../../tools/forecast/duplicateSubcontract";
import { addUpdateSubcontract } from "../../../tools/forecast/addUpdateSubcontract";
import getSubcontractData from "../../../tools/forecast/getSubcontractData";
import { ISubcontract } from "../../../interface/subcontract";

// Icons
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EngineeringIcon from '@mui/icons-material/Engineering';

const Subcontract = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const token = useSelector((state: RootState) => state.user.token);
    const dispatch = useDispatch();
    const params = useParams();

    // States
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [subcontractData, setSubcontractData] = useState<ISubcontract[]>([]);
    const [agency, setAgency] = useState<string | null>("All");
    const [activeTypes, setActiveTypes] = useState<string[]>(["Tout"]);
    const [selectedSubcontract, setSelectedSubcontract] = useState<string[]>([]);
    const [editId, setEditId] = useState<string>("");
    const [searchLabel, setSearchLabel] = useState<string>("");

    const monthList = getForecastMonthList(forecastDates);
    const headers = ["Tout", "Forfait", "Régie"];
    const agencies = [
        { label: "Toutes les agences", value: "All" },
        { label: "Agence Nord", value: "Nord" },
        { label: "Agence Sud", value: "Sud" }
    ];

    // Actions
    const confirmDelete = useCallback(async () => {
        try {
            await Promise.all(
                selectedSubcontract.map(id => 
                    axios.delete(`${urlAPI}/forecast/subcontract/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                )
            );
            setShowDeleteDialog(false);
            setSelectedSubcontract([]);
            dispatch(showSnackbar("Supprimé avec succès.", "success"));
        } catch (error) {
            dispatch(showSnackbar("Erreur lors de la suppression.", "error"));
        }
    }, [selectedSubcontract, token, dispatch]);

    const handleDuplication = useCallback(() => {
        duplicateSubcontract(selectedSubcontract[0], token, setOpenModal, dispatch);
        setSelectedSubcontract([]);
    }, [selectedSubcontract, token, dispatch]);

    useEffect(() => {
        getSubcontractData(params.id, forecastVersion, agency, setSubcontractData, token, dispatch, () => {})
    }, [forecastVersion, agency, openModal, showDeleteDialog, handleDuplication]);

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            {/* HEADER DESIGN */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <EngineeringIcon sx={{ color: "#f59e0b" }} />
                        <Typography variant="h5" fontWeight={800}>Sous-Traitance</Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    {/* Recherche */}
                    <TextField 
                        size="small" placeholder="Rechercher un prestataire..." value={searchLabel} onChange={(e) => setSearchLabel(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, width: 250 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment> }}
                    />

                    {/* Filtre Agence */}
                    <Select
                        size="small" value={agency} onChange={(e) => setAgency(e.target.value)}
                        sx={{ width: 180, bgcolor: "#f1f5f9", borderRadius: 2, ".MuiOutlinedInput-notchedOutline": { border: "none" } }}
                    >
                        {agencies.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>

                    {/* Filtre Type */}
                    <Select
                        size="small" multiple value={activeTypes}
                        onChange={(e: any) => {
                            const val = e.target.value;
                            setActiveTypes(val.includes("Tout") ? ["Tout"] : val.filter((v: string) => v !== "Tout"));
                        }}
                        renderValue={(selected) => selected.join(", ")}
                        sx={{ width: 180, bgcolor: "#f1f5f9", borderRadius: 2, ".MuiOutlinedInput-notchedOutline": { border: "none" } }}
                    >
                        {headers.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                <Checkbox size="small" checked={activeTypes.includes(opt)} /> {opt}
                            </MenuItem>
                        ))}
                    </Select>

                    <Box sx={{ flexGrow: 1 }} />
                    
                    <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => { setEditId(""); setOpenModal(true); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                        Nouveau prestataire
                    </Button>
                </Stack>
            </Box>

            {/* CONTENU / TABLEAUX */}
            <Box sx={{ mb: 12 }}>
                {(activeTypes.includes("Tout") ? headers.slice(1) : activeTypes).map((type) => (
                    <SubcontractData
                        key={type}
                        forecastVersion={forecastVersion}
                        forecastDates={forecastDates}
                        type={type}
                        agency={agency}
                        selectedSubcontract={selectedSubcontract}
                        setSelectedSubcontract={setSelectedSubcontract}
                        openModal={openModal}
                        showDeleteDialog={showDeleteDialog}
                        setEditId={setEditId}
                        setOpenModal={setOpenModal}
                        // On filtre ici par le type ET par la recherche searchLabel
                        subcontracts={subcontractData
                            .filter(sub => (sub.typeFrais || "Forfait") === type)
                            .filter(sub => sub.label.toLowerCase().includes(searchLabel.toLowerCase()))
                        }
                    />
                ))}
            </Box>

            {/* DOCK FLOTTANT */}
            {selectedSubcontract.length > 0 && (
                <Box sx={{ position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", display: "flex", alignItems: "center", gap: 2, bgcolor: "white", p: "12px 24px", borderRadius: "100px", boxShadow: "0px 10px 40px rgba(0,0,0,0.15)", zIndex: 1100, border: "1px solid #e2e8f0" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mr: 1 }}>{selectedSubcontract.length} sélectionnés</Typography>
                    <Divider orientation="vertical" flexItem />
                    <Tooltip title="Supprimer"><Fab color="error" size="small" onClick={() => setShowDeleteDialog(true)}><DeleteIcon /></Fab></Tooltip>
                    {selectedSubcontract.length === 1 && (
                        <Tooltip title="Dupliquer"><Fab color="info" size="small" onClick={handleDuplication}><ContentCopyIcon sx={{ color: "white" }}/></Fab></Tooltip>
                    )}
                    <IconButton size="small" onClick={() => setSelectedSubcontract([])}><CloseIcon fontSize="small" /></IconButton>
                </Box>
            )}

            {/* MODALES */}
            <Dialog open={openModal} fullWidth maxWidth="xl">
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: "1px solid #e2e8f0" }}>
                    <Typography variant="h6" fontWeight={800}>{editId ? "Modifier" : "Ajouter"} un prestataire</Typography>
                    <IconButton onClick={() => setShowConfirmDialog(true)}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateSubcontract subcontractId={editId} handleSubmit={addUpdateSubcontract} forecastId={params.id} version={forecastVersion} setOpenModal={setOpenModal} monthList={monthList} setShowConfirmDialog={setShowConfirmDialog} />
                </DialogContent>
            </Dialog>

            <AlerteDialog open={showConfirmDialog} handleClose={() => setShowConfirmDialog(false)} deleteProject={() => { setShowConfirmDialog(false); setOpenModal(false); setEditId("") }} title="Abandonner ?" contentText="Voulez-vous quitter sans enregistrer ?" color="error" />
            <AlerteDialog open={showDeleteDialog} handleClose={() => setShowDeleteDialog(false)} deleteProject={confirmDelete} title="Supprimer ?" contentText={`Supprimer définitivement les ${selectedSubcontract.length} lignes ?`} color="error" />
        </Box>
    );
};

export default Subcontract;